// @flow

import http from "@http";
import config from "@config";
import type { HTTPRequestResponceType } from "@types";

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import { route } from "@routes/helper";
import actionCreators from "@modules/service/actionCreator";
import { isFirstCareUser } from "@modules/account/selectors";
import { getRTWLiveProcessesFilter } from "@modules/service/selectors";
import {
  GetRtw,
  GetRtwSchema,
  AddRtw,
  StartDraftRtw,
  DuplicateRtw,
  UpdateRtw,
  UpdateRtwAssignments,
  DisableRtw,
  DiscardRtwDraft,
  SubmitRtwForReview,
  CancelRtwReview,
  CancelRtwReviewAndDiscard,
  GetRTWLiveProcesses,
  GetRTWPendingProcesses,
  GetRTWDraftProcesses,
} from "@modules/service/constants/actions";
import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { Rtw, RTWFilterStateType } from "@modules/service/reducer/rtw/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ItemResponse<T> = $Exact<T>;

// READ

type RtwListRequestType = $Exact<{
  clientId: string,
  page?: number,
  isActive?: RTWFilterStateType,
}>;

export function* onGetRTWLiveProcesses({
  payload,
}: Action<RtwListRequestType>): Generator<*, void, HTTPRequestResponceType> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const is_active = yield select(getRTWLiveProcessesFilter);

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/active`,
      {
        params: {
          page: payload.page || 1,
          limit: config.resultsPerPage,
          is_active,
        },
      },
    );

    yield put(actionCreators.GetRTWLiveProcessesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetRTWLiveProcessesError(e.response));
  }
}

export function* onGetRTWPendingProcesses({
  payload,
}: Action<RtwListRequestType>): Generator<*, void, HTTPRequestResponceType> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/pending-review`,
      {
        params: {
          page: payload.page || 1,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetRTWPendingProcessesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetRTWPendingProcessesError(e.response));
  }
}

export function* onGetRTWDraftProcesses({
  payload,
}: Action<RtwListRequestType>): Generator<*, void, HTTPRequestResponceType> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/draft`,
      {
        params: {
          page: payload.page || 1,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetRTWDraftProcessesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetRTWDraftProcessesError(e.response));
  }
}

type RtwRequest = RtwListRequestType &
  $Exact<{
    processId: string,
  }>;

export function* onGetRtw({
  payload,
}: Action<RtwRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Rtw>> }> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/assignment`,
    );

    yield all([
      put(actionCreators.GetRtwSuccess(stableResponse.data)),
      put(actionCreators.SetRtwAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetRtwError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  processId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(actionCreators.GetRTWLiveProcesses({ clientId: params.clientId }));
  yield put(actionCreators.GetRTWPendingProcesses({ clientId: params.clientId }));
  yield put(actionCreators.GetRTWDraftProcesses({ clientId: params.clientId }));

  if (params.processId) {
    yield put(
      actionCreators.GetRtw({
        clientId: params.clientId,
        processId: params.processId,
      }),
    );
  }
}

// CREATE

type GetRtwSchemaRequest = $Exact<{
  clientId?: number,
}>;

export function* onGetRtwSchema({
  payload,
}: Action<GetRtwSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("RtwInterviewProcessRevisionDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetRtwSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetRtwSchemaError(e.response));
  }
}

export function* onStartDraftRtw({
  payload,
}: Action<RtwRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Rtw>> }> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}`,
    );

    yield all([
      put(actionCreators.StartDraftRtwSuccess(response.data)),
      put(actionCreators.SetShowEditRtwModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftRtwError(e.response));
  }
}

export function* onDuplicateRtw({
  payload,
}: Action<RtwRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Rtw>> }> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateRtwSuccess(response.data)),
      put(actionCreators.SetRtwAssignments([])),
      put(actionCreators.SetShowAddRtwModal(true)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateRtwError(e.response));
  }
}

type RtwAddRequest = RtwListRequestType &
  $Exact<{
    draft: Rtw,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddRtw({ payload }: Action<RtwAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process`,
      payload.draft,
    );
    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${response.data.id}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.AddRtwSuccess(response.data)),
      put(actionCreators.SetShowAddRtwModal(false)),
      put(sendSuccessMessage("DRAFT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddRtwError(e.response));
  }
}

// UPDATE

type RtwUpdateRequest = RtwRequest &
  $Exact<{
    draft: Rtw,
  }>;

export function* onUpdateRtw({ payload }: Action<RtwUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.UpdateRtwSuccess(response.data)),
      put(actionCreators.SetShowEditRtwModal(false)),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateRtwError(e.response));
  }
}

type RtwUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<Rtw>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateRtwAssignments({
  payload,
}: Action<RtwUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/rtw-interview/process/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/rtw-interview/process/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/rtw-interview/process/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      processId: payload.container.id,
    });

    yield put(actionCreators.SetShowEditRtwAssignmentsModal(false));
  } catch (e) {
    yield put(actionCreators.UpdateRtwAssignmentsError(e.response));
  }
}

// DISABLE

export type RtwDisableRequest = RtwRequest &
  $Exact<{
    approvable: ApprovableContainer<Rtw>,
  }>;

export function* onDisable({
  payload,
}: Action<RtwDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Rtw>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/draft`,
      {
        ...draft,
        isActive: !draft.isActive,
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/submit-for-review`,
      {
        notes: "Rtw disabled and resubmitted for review",
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.DisableRtwSuccess(response.data)),
      put(actionCreators.SetShowDisableRtwModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
    yield put(sendSuccessMessage("DRAFT_CREATED"));
  } catch (e) {
    yield put(actionCreators.DisableRtwError(e.response));
  }
}

// STATE TRANSITIONS

export type RtwTransitionRequest = $Exact<{
  clientId: string,
  processId: string,
}>;

export function* onDiscardDraft({
  payload,
}: Action<RtwTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/draft`,
    );
    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.DiscardRtwDraftSuccess()),
      put(actionCreators.SetShowDiscardRtwModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardRtwDraftError(e.response));
  }
}

export type RtwSubmitForReviewRequest = $Exact<{
  ...RtwTransitionRequest,
  notes: string,
}>;

export function* onSubmitForReview({
  payload,
}: Action<RtwSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.SubmitRtwForReviewSuccess()),
      put(actionCreators.SetShowSubmitRtwModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitRtwForReviewError(e.response));
  }
}

export function* onCancelReview({
  payload,
}: Action<RtwTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/cancel-review`,
    );
    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.CancelRtwReviewSuccess()),
      put(actionCreators.SetShowCancelRtwModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelRtwReviewError(e.response));
  }
}

export function* onCancelReviewAndDiscard({
  payload,
}: Action<RtwTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/draft`,
    );

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.CancelRtwReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardRtwModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelRtwReviewError(e.response));
  }
}

// ROOT

export function* RtwSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetRTWLiveProcesses, onGetRTWLiveProcesses),
    takeEvery(GetRTWPendingProcesses, onGetRTWPendingProcesses),
    takeEvery(GetRTWDraftProcesses, onGetRTWDraftProcesses),
    takeEvery(GetRtw, onGetRtw),
    takeEvery(GetRtwSchema, onGetRtwSchema),
    takeEvery(AddRtw, onAddRtw),
    takeEvery(StartDraftRtw, onStartDraftRtw),
    takeEvery(DuplicateRtw, onDuplicateRtw),
    takeEvery(UpdateRtw, onUpdateRtw),
    takeEvery(UpdateRtwAssignments, onUpdateRtwAssignments),
    takeEvery(DisableRtw, onDisable),
    takeEvery(DiscardRtwDraft, onDiscardDraft),
    takeEvery(SubmitRtwForReview, onSubmitForReview),
    takeEvery(CancelRtwReview, onCancelReview),
    takeEvery(CancelRtwReviewAndDiscard, onCancelReviewAndDiscard),
  ]);
}

export default RtwSagas;
